import { Footer } from "@/components/Footer";
import { PageMeta } from "@/components/site/blocks";
import { Band, Closing, Faqs, H3, Hero, In, JsonLd, List, P, Related, Src, Steps, Table, WhatIs, WorkflowCard, articleLd } from "@/components/seo/kit";

// /guides/forward-email-to-slack-channel. Plan: gtm/products/clearmakes-inbox/seo/pages/forward-email-to-slack-channel.md
// A how-to guide: the free ways first, then the faster way with ClearMakes Inbox.
// Facts opened on 2026-09-29; sources are linked on the page.

const PATH = "/guides/forward-email-to-slack-channel";
const TITLE = "How to Forward Emails to a Slack Channel Automatically";
const DESC = "Forward emails to a Slack channel automatically: Slack's channel address, a Gmail or Outlook rule, or a workflow. Steps, costs and what each one misses.";

const faqs = [
  {
    q: "Can I forward emails to a Slack channel on the free Slack plan?",
    a: "Not with a channel email address: Slack keeps those for paid plans. On the free plan you can send single emails with Slack's Gmail or Outlook add-on, use a Slackbot forwarding address for your own messages, or use an automation tool that posts through a connected Slack app.",
  },
  {
    q: "Why do my forwarded Outlook emails bounce?",
    a: "Many Microsoft 365 organisations block automatic forwarding to outside addresses, and a Slack address counts as outside. The sender gets a 5.7.520 “Access denied” bounce. Your admin can allow it, or you can use a workflow that posts to Slack instead.",
  },
  {
    q: "How do I forward only some emails?",
    a: "In Gmail, create a filter with your search (for example from:customer.com) and choose “Forward it”. In Outlook, use a rule. With a workflow, describe which emails count in plain words, such as “only emails from customers”.",
  },
  {
    q: "Can Slack show a summary instead of the whole email?",
    a: "Forwarding copies the whole email. To post a short summary, the sender and a link instead, you need an automation step that writes the post, for example a ClearMakes Inbox workflow.",
  },
];

const Guide = () => (
  <>
    <PageMeta
      title={TITLE}
      description={DESC}
      path={PATH}
      crumbs={[
        { name: "Home", path: "/" },
        { name: "Guides", path: "/guides" },
        { name: "Forward emails to a Slack channel", path: PATH },
      ]}
    />
    <JsonLd data={articleLd(TITLE, DESC, PATH)} />
    <main>
      <Hero
        eyebrow="Guide · Email and Slack"
        title="How to forward emails to a Slack channel automatically"
        lead="Four ways, from free to fully automatic, with the steps for each and what each one can't do. Pick the simplest one that fits."
        primary={null}
        secondary={{ label: "Skip to the one-sentence way", href: "#workflow" }}
      />

      <WhatIs title="What does forwarding email to Slack mean?">
        Forwarding email to Slack means new emails appear in a Slack channel automatically, so the team sees them without anyone copying them
        across. Slack gives paid workspaces an email address per channel; Gmail filters and Outlook rules can send mail there. Automation tools,
        such as ClearMakes Inbox, can also choose which emails to post and summarise them.
      </WhatIs>

      <Band
        tone="pale"
        title="Way 1: get the channel's email address in Slack"
        answer="On a paid Slack plan, every channel or direct message can have its own email address. Anything sent to it appears in the conversation."
      >
        <Steps
          items={[
            { title: "Open the channel", body: "Click the channel name in the header." },
            { title: "Open the Automations tab", body: "Choose “Send emails to this channel”, then “Get email address”." },
            { title: "Copy the address", body: "You will paste it into Gmail or Outlook in Way 2 or 3." },
          ]}
        />
        <P>
          Slack says channel addresses need a paid subscription, and HIPAA-compliant Enterprise organisations can't send email to Slack. Free
          workspaces can use Slack's Gmail or Outlook add-on (one email at a time) or a Slackbot forwarding address (into your own Slackbot
          messages, not a channel). Source: <Src href="https://slack.com/help/articles/206819278-Send-emails-to-Slack">Slack Help, Send emails to Slack</Src>, checked 29 September 2026.
        </P>
      </Band>

      <Band
        tone="white"
        title="Way 2: a Gmail filter that forwards to Slack"
        answer="Gmail can forward every email, or only the ones a filter matches, to the Slack address. You add the address once, confirm it, and then build the filter."
      >
        <Steps
          items={[
            { title: "Add the Slack address as a forwarding address", body: "Gmail Settings, “Forwarding and POP/IMAP”, then “Add a forwarding address”." },
            { title: "Confirm it", body: "Gmail sends a verification message to the new address, so it arrives in your Slack channel. Open it there and follow the link." },
            { title: "Forward only what matters", body: "Leave full forwarding off. Search for the emails you want (for example from:yourclient.com), choose “Create filter”, tick “Forward it” and pick the Slack address." },
          ]}
        />
        <P>
          Google notes that automatic forwarding sends every message unless you use a filter. Source:{" "}
          <Src href="https://support.google.com/mail/answer/10957">Gmail Help, automatically forward messages</Src>, checked 29 September 2026.
        </P>
      </Band>

      <Band
        tone="pale"
        title="Way 3: an Outlook rule that forwards to Slack"
        answer="An Outlook rule can forward matching emails to the Slack address, but many Microsoft 365 organisations block automatic forwarding to outside addresses. Test it with one email before you rely on it."
        narrow
      >
        <List
          items={[
            "Create a rule: when a message arrives that matches your conditions, forward it to the Slack address.",
            "If the test bounces with 5.7.520 “Access denied”, your organisation blocks external forwarding. Microsoft turned it off by default for new organisations in 2021.",
            "Ask your admin to allow it for this one address, or use a connected app instead (Way 4).",
          ]}
        />
        <P>
          Source: <Src href="https://learn.microsoft.com/en-us/defender-office-365/outbound-spam-policies-external-email-forwarding">Microsoft Learn, control external email forwarding</Src>, checked 29 September 2026.
        </P>
      </Band>

      <Band
        tone="white"
        id="workflow"
        title="Way 4: a workflow that posts to Slack (works on free Slack)"
        answer="An automation tool reads the mailbox through a connected app and posts to Slack itself, so it needs no channel address and no forwarding rule. It can also choose the emails and write a short summary."
      >
        <WorkflowCard
          sentence="When a customer emails support@, post the sender, subject and a two-line summary to #support in Slack."
          steps={["When an email arrives in Gmail", "Check it is from a customer", "Summarise it", "Post to #support in Slack"]}
          label="In ClearMakes Inbox"
        />
        <H3>With ClearMakes Inbox</H3>
        <Steps
          items={[
            { title: "Connect Gmail (or Outlook) and Slack", body: <>In the Apps page of <In href="/products/inbox">ClearMakes Inbox</In>. The free plan allows 3 apps.</> },
            { title: "Type the sentence", body: "Describe which emails, which channel and what the post should say." },
            { title: "Check the steps and switch it on", body: "Inbox asks at most one question. Every run is then listed with its steps." },
          ]}
        />
        <P>
          Other tools do this too. Zapier's free plan runs two-step Zaps and checks for new email every 15 minutes; its Professional plan is
          $29.99 a month for 750 tasks. ClearMakes Inbox's free plan includes 100 runs a month, and Starter is $29 a month for 5,000 runs.
          Sources: <Src href="https://zapier.com/pricing">Zapier pricing</Src> (checked 29 September 2026) and <In href="/pricing">ClearMakes Inbox pricing</In>.
        </P>
      </Band>

      <Band tone="pale" title="Which way should you pick?" answer="Use the free ways when a plain copy is enough. Use a workflow when you need to choose, summarise or route emails, or when Slack is on the free plan.">
        <Table
          caption="Ways to forward email to Slack compared"
          head={["Way", "Slack plan", "Chooses emails", "Summary", "Main catch"]}
          rows={[
            ["Slack channel address + Gmail filter", "Paid", "Yes, with a filter", "No", "Whole email copied"],
            ["Slack channel address + Outlook rule", "Paid", "Yes, with a rule", "No", "Often blocked in Microsoft 365 (5.7.520)"],
            ["Slack Gmail/Outlook add-on", "Any", "You pick each email", "No", "Manual, one email at a time"],
            ["Workflow (ClearMakes Inbox, Zapier, Make)", "Any", "Yes", "Yes, with a step", "Another tool to connect and pay for above its free plan"],
          ]}
        />
        <H3>Checklist before you switch anything on</H3>
        <List
          items={[
            "Pick one channel per kind of email, so people know where to look.",
            "Decide who replies: forwarding to Slack does not answer the customer.",
            "Keep private emails out: forward by sender or subject, never everything.",
            "Send a test email and read what arrives in Slack.",
          ]}
        />
      </Band>

      <Faqs items={faqs} />

      <Related
        links={[
          { href: "/integrations/gmail-to-slack", label: "Gmail to Slack automation", desc: "The workflow in detail: permissions, limits, prices." },
          { href: "/integrations/outlook-to-slack", label: "Outlook to Slack automation", desc: "No forwarding rule, so nothing bounces." },
          { href: "/integrations/outlook-to-teams", label: "Post Outlook emails to Teams", desc: "The same job for Microsoft Teams." },
          { href: "/guides", label: "All guides", desc: "More step-by-step guides for email and chat work." },
        ]}
      />

      <Closing title="Want it to choose and summarise for you?" line="Connect your mailbox and Slack, type one sentence, and check the steps." />
    </main>
    <Footer />
  </>
);

export default Guide;
